/// Auto-generated with ScanbotSDKCodegenV3. Modifications will be overwritten.
/// Generated from core/schemas/DocumentQualityAnalyzerTypes.yaml
import { ToJsonConfiguration } from '../common/JsonSerializationTypes';
import { ImageRef } from '../image/image';
/** @hidden */
export const DocumentQualityValues = [
    'VERY_POOR',
    'POOR',
    'REASONABLE',
    'GOOD',
    'EXCELLENT',
];
/**
Point in the quality-number of symbols space to separate quality levels.
*/
export class DocumentQualityThreshold {
    /** @param source {@displayType `DeepPartial<DocumentQualityThreshold>`} */
    constructor(source = {}) {
        if (source.symbolQuality !== undefined) {
            this.symbolQuality = source.symbolQuality;
        }
        else {
            throw new Error('symbolQuality must be present in constructor argument');
        }
        if (source.symbolRatio !== undefined) {
            this.symbolRatio = source.symbolRatio;
        }
        else {
            throw new Error('symbolRatio must be present in constructor argument');
        }
    }
}
/**
Configuration for processing image by tiles.
This can be used to make the document quality analyzer processing faster with lower memory consumption,
but prediction can be less accurate.
*/
export class ProcessByTileConfiguration {
    /** @param source {@displayType `DeepPartial<ProcessByTileConfiguration>`} */
    constructor(source = {}) {
        /**
          Enable processing by tiles.
          
          Default is false
          */
        this.enabled = false;
        /**
          At least this fraction of the image will be processed, range is from 0 to 1.
          Decreasing this value below 1.0 can lead to faster processing but may result in
          less accurate prediction, as only a subset of the image tiles will be analyzed.
          
          Default is 1
          */
        this.minProcessedFraction = 1.0;
        /**
          At most this fraction of the image will be processed, range is from 0 to 1.
          Decreasing this value below 1.0 can lead to faster processing but may result in
          less accurate prediction, as only a subset of the image tiles will be analyzed.
          
          Default is 1
          */
        this.maxProcessedFraction = 1.0;
        /**
          If this number of symbols is found and minProcessedFraction of the image is processed, the processing stops.
          
          Default is 100
          */
        this.minNumberOfRequiredSymbols = 100;
        /**
          Image will be processed in tiles of this size; will be ignored if image is small.
          
          Default is 300
          */
        this.tileSize = 300;
        if (source.enabled !== undefined) {
            this.enabled = source.enabled;
        }
        if (source.minProcessedFraction !== undefined) {
            this.minProcessedFraction = source.minProcessedFraction;
        }
        if (source.maxProcessedFraction !== undefined) {
            this.maxProcessedFraction = source.maxProcessedFraction;
        }
        if (source.minNumberOfRequiredSymbols !== undefined) {
            this.minNumberOfRequiredSymbols = source.minNumberOfRequiredSymbols;
        }
        if (source.tileSize !== undefined) {
            this.tileSize = source.tileSize;
        }
    }
}
/**
Document Quality Analyzer configuration.
*/
export class DocumentQualityAnalyzerConfiguration {
    /** @param source {@displayType `DeepPartial<DocumentQualityAnalyzerConfiguration>`} */
    constructor(source = {}) {
        /**
          Quality thresholds to separate quality levels.
          */
        this.qualityThresholds = [
            new DocumentQualityThreshold({ symbolQuality: 0.5, symbolRatio: 0.5 }),
            new DocumentQualityThreshold({ symbolQuality: 0.7, symbolRatio: 0.3 }),
            new DocumentQualityThreshold({ symbolQuality: 0.85, symbolRatio: 0.3 }),
            new DocumentQualityThreshold({ symbolQuality: 0.9, symbolRatio: 0.1 }),
        ];
        /**
          quality levels.
          */
        this.qualityIndices = ['VERY_POOR', 'POOR', 'REASONABLE', 'GOOD', 'EXCELLENT'];
        /**
          Enable orientation detection. Document orientation will be returned in `DocumentQualityAnalyzerResult.orientation`.
          
          Default is false
          */
        this.detectOrientation = false;
        /**
          Inspect small text (typically less than 12 pixels height) when analyzing the document quality.
          If true, processing will be slower, but when the quality issue with a document is the low resolution of the text,
          then the issue will be caught.
          
          Default is false
          */
        this.inspectSmallText = false;
        /**
          Maximum image size in pixels, if image is bigger, it will be resized. Zero or negative value means no limit.
          
          Default is 2000
          */
        this.maxImageSize = 2000;
        /**
          If estimated number of symbols is less than this value, return that document is not found.
          
          Default is 20
          */
        this.minEstimatedNumberOfSymbolsForDocument = 20;
        /**
          Configuration for processing image by tiles.
          */
        this.processByTileConfiguration = new ProcessByTileConfiguration({});
        /**
          If detectOrientation is True, minNumberOfRequiredSymbols is found, and orientationConfidence is bigger than this threshold, the processing stops.
          If the maxProcessedFraction part of the image is processed and orientationConfidence is lower than the threshold, the returned orientation will be 0.
          
          Default is 0
          */
        this.minRequiredOrientationConfidence = 0.0;
        /**
          If true, will return the quality heatmap as a part of the result.
          In this case, the entire image will be processed and the minProcessedFraction will be ignored.
          Useful for debugging situations where the returned quality doesn't appear to match the perceived quality.
          
          Default is false
          */
        this.returnQualityHeatmap = false;
        if (source.qualityThresholds !== undefined) {
            this.qualityThresholds = source.qualityThresholds.map((it) => {
                return new DocumentQualityThreshold(it);
            });
        }
        if (source.qualityIndices !== undefined) {
            this.qualityIndices = source.qualityIndices.map((it) => {
                return it;
            });
        }
        if (source.detectOrientation !== undefined) {
            this.detectOrientation = source.detectOrientation;
        }
        if (source.inspectSmallText !== undefined) {
            this.inspectSmallText = source.inspectSmallText;
        }
        if (source.maxImageSize !== undefined) {
            this.maxImageSize = source.maxImageSize;
        }
        if (source.minEstimatedNumberOfSymbolsForDocument !== undefined) {
            this.minEstimatedNumberOfSymbolsForDocument = source.minEstimatedNumberOfSymbolsForDocument;
        }
        if (source.processByTileConfiguration !== undefined) {
            this.processByTileConfiguration = new ProcessByTileConfiguration(source.processByTileConfiguration);
        }
        if (source.minRequiredOrientationConfidence !== undefined) {
            this.minRequiredOrientationConfidence = source.minRequiredOrientationConfidence;
        }
        if (source.returnQualityHeatmap !== undefined) {
            this.returnQualityHeatmap = source.returnQualityHeatmap;
        }
    }
}
/**
Result of document quality analysis. It can be used to determine, e.g., if a document is good enough to be used for OCR processing.
*/
export class DocumentQualityAnalyzerResult {
    /** @param source {@displayType `DeepPartial<DocumentQualityAnalyzerResult>`} */
    constructor(source = {}) {
        /**
          Quality heatmap.
          Black marks areas of no detected text. Colors represent the quality of detected text,
          with red meaning the lowest quality and yellow and green meaning progressively higher quality.
          */
        this.qualityHeatmap = null;
        this._released = false;
        if (source.documentFound !== undefined) {
            this.documentFound = source.documentFound;
        }
        else {
            throw new Error('documentFound must be present in constructor argument');
        }
        if (source.quality !== undefined) {
            this.quality =
                source.quality != null ? (DocumentQualityValues.includes(source.quality) ? source.quality : null) : null;
        }
        else {
            throw new Error('quality must be present in constructor argument');
        }
        if (source.orientation !== undefined) {
            this.orientation = source.orientation != null ? source.orientation : null;
        }
        else {
            throw new Error('orientation must be present in constructor argument');
        }
        if (source.orientationConfidence !== undefined) {
            this.orientationConfidence = source.orientationConfidence != null ? source.orientationConfidence : null;
        }
        else {
            throw new Error('orientationConfidence must be present in constructor argument');
        }
        if (source.cumulativeQualityHistogram !== undefined) {
            this.cumulativeQualityHistogram = source.cumulativeQualityHistogram.map((it) => {
                return it;
            });
        }
        else {
            throw new Error('cumulativeQualityHistogram must be present in constructor argument');
        }
        if (source.cumulativeContrastHistogram !== undefined) {
            this.cumulativeContrastHistogram = source.cumulativeContrastHistogram.map((it) => {
                return it;
            });
        }
        else {
            throw new Error('cumulativeContrastHistogram must be present in constructor argument');
        }
        if (source.cumulativeTextSizeHistogram !== undefined) {
            this.cumulativeTextSizeHistogram = source.cumulativeTextSizeHistogram.map((it) => {
                return it;
            });
        }
        else {
            throw new Error('cumulativeTextSizeHistogram must be present in constructor argument');
        }
        if (source.qualityHeatmap !== undefined) {
            this.qualityHeatmap = source.qualityHeatmap != null ? ImageRef.from(source.qualityHeatmap) : null;
        }
    }
    async serialize(config = new ToJsonConfiguration()) {
        return {
            documentFound: this.documentFound,
            quality: this.quality != null ? this.quality : null,
            orientation: this.orientation != null ? this.orientation : null,
            orientationConfidence: this.orientationConfidence != null ? this.orientationConfidence : null,
            cumulativeQualityHistogram: this.cumulativeQualityHistogram.map((it) => {
                return it;
            }),
            cumulativeContrastHistogram: this.cumulativeContrastHistogram.map((it) => {
                return it;
            }),
            cumulativeTextSizeHistogram: this.cumulativeTextSizeHistogram.map((it) => {
                return it;
            }),
            qualityHeatmap: config.serializeImages
                ? this.qualityHeatmap != null
                    ? await this.qualityHeatmap.serialize(config.imageSerializationMode)
                    : null
                : undefined,
        };
    }
    release() {
        if (this._released) {
            return;
        }
        {
            if (this.qualityHeatmap != null) {
                this.qualityHeatmap.release();
            }
        }
        this._released = true;
    }
    async encodeImages() {
        if (this.qualityHeatmap != null) {
            await this.qualityHeatmap.encodeInPlace();
        }
    }
}
//# sourceMappingURL=DocumentQualityAnalyzerTypes.js.map